import * as React from "react";
import styled from "@emotion/styled";
import { Button, AppError, FlexRow, Text } from "atoms";
import { ApiError } from "utils/api";
import { pluralize } from "utils/text";
import { Review } from "./Review";
import { useReviews } from "../api/useReviews";
import type { ArtistReview } from "../types";
import { ReviewSort, useReviewSort } from "./ReviewSort";

type ReviewListProps = {
  artistId: string;
  reviewCount: number;
  writeAReviewButton?: React.ReactNode;
} & ReturnType<typeof useReviewSort>;

function ReviewList({
  artistId,
  reviewCount,
  activeReviewSort,
  writeAReviewButton,
  createReviewSortHandler,
  ...props
}: ReviewListProps) {
  const { data, hasNextPage, fetchNextPage, isFetching, isError, error } =
    useReviews({
      artistId,
      sortBy: activeReviewSort,
    });

  let allReviews: ArtistReview[] = [];
  if (data?.pages) {
    allReviews = allReviews.concat(...data.pages.map((page) => page.data));
  }

  const loadMore = () => fetchNextPage();

  return (
    <section {...props}>
      {reviewCount > 0 && (
        <FlexRow justifyContent="space-between" alignItems="center">
          <ReviewCount size="xxs">
            {reviewCount > 10 ? (
              <span>
                Showing {allReviews.length} of {reviewCount} reviews
              </span>
            ) : (
              <span>{pluralize(reviewCount, "review")}</span>
            )}
          </ReviewCount>
          <FlexRow gap="10px">
            {writeAReviewButton}
            <ReviewSort createHandler={createReviewSortHandler} />
          </FlexRow>
        </FlexRow>
      )}

      {allReviews.length &&
        allReviews.map((review) => (
          <Review key={review.reviewId} review={review} />
        ))}

      {isError && (
        <AppError>
          {error instanceof ApiError
            ? error.message
            : "Sorry, we had a problem loading reviews."}
        </AppError>
      )}

      {hasNextPage && (
        <LoadMoreButton
          variant="primary"
          size="sm"
          onClick={loadMore}
          disabled={isFetching}
        >
          Load More Reviews...
        </LoadMoreButton>
      )}
    </section>
  );
}

const ReviewCount = styled(Text)({
  margin: 0,
});

const LoadMoreButton = styled(Button)({
  marginBottom: "1rem",
});

export { ReviewList };
